import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Target, TrendingUp, Clock, X } from "lucide-react";

const cases = [
  {
    title: "Refurbished Mobile Store",
    client: "Tier-2 city retail · Maharashtra",
    image:
      "https://images.unsplash.com/photo-1511707171634-5f897ff02aa9?auto=format&fit=crop&w=1400&q=70",
    accent: "Mobile-first eCommerce",
    goal:
      "Owner was relying entirely on Instagram DMs. Wanted a trustable online catalogue so customers could browse stock, see warranty info, and pay online without messaging.",
    services: ["E-Commerce", "WhatsApp checkout", "On-page SEO", "Razorpay"],
    timeline: "12 days",
    before: [
      { k: "Monthly inquiries", v: "~30 (via DMs)" },
      { k: "Online sales", v: "₹0" },
      { k: "Trust signals", v: "None — no reviews, no warranty page" },
      { k: "Search visibility", v: "Not indexed" },
    ],
    after: [
      { k: "Monthly inquiries", v: "120+ (qualified)" },
      { k: "Online sales", v: "₹2.4L in month 1" },
      { k: "Trust signals", v: "Reviews, warranty, return policy" },
      { k: "Search visibility", v: "Ranks for 14 local terms" },
    ],
    metrics: [
      { label: "More inquiries", value: "+300%" },
      { label: "First-month revenue", value: "₹2.4L" },
      { label: "Bounce rate", value: "-46%" },
    ],
  },
  {
    title: "Local Business Website",
    client: "Family-run cafe & bakery · Mangalore",
    image:
      "https://images.unsplash.com/photo-1554118811-1e0d58224f24?auto=format&fit=crop&w=1400&q=70",
    accent: "Brand + booking website",
    goal:
      "Bring footfall back after a slow season. Establish a premium brand presence with the menu, photos, table reservations and direct WhatsApp ordering.",
    services: ["Brand site", "Online menu", "Reservation form", "WhatsApp orders"],
    timeline: "9 days",
    before: [
      { k: "Online presence", v: "Just a Google listing" },
      { k: "Weekend covers", v: "60–80 / weekend" },
      { k: "Reservation channel", v: "Phone only" },
      { k: "Photos / menu", v: "Outdated PDFs" },
    ],
    after: [
      { k: "Online presence", v: "Branded site + Insta-linked menu" },
      { k: "Weekend covers", v: "150+ / weekend" },
      { k: "Reservation channel", v: "Form + WhatsApp auto-confirm" },
      { k: "Photos / menu", v: "Pro shoot + live menu" },
    ],
    metrics: [
      { label: "Walk-ins", value: "+85%" },
      { label: "Online reservations", value: "40 / mo" },
      { label: "Avg. session time", value: "2m 14s" },
    ],
  },
  {
    title: "Service Business Website",
    client: "Skin & Hair Clinic · Bengaluru",
    image:
      "https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=1400&q=70",
    accent: "Lead-gen + appointment booking",
    goal:
      "Reduce dependency on paid ads. Generate 30+ qualified leads/month organically, with a calm, premium clinic feel that builds patient trust before they walk in.",
    services: ["Lead funnel", "Appointment booking", "SEO blog", "Analytics"],
    timeline: "16 days",
    before: [
      { k: "Lead source", v: "100% paid ads (₹40k/mo)" },
      { k: "Monthly inquiries", v: "~25" },
      { k: "Brand perception", v: "Generic / clinical" },
      { k: "Online booking", v: "Not available" },
    ],
    after: [
      { k: "Lead source", v: "60% organic, 40% paid" },
      { k: "Monthly inquiries", v: "70+ qualified" },
      { k: "Brand perception", v: "Premium, calm, trustworthy" },
      { k: "Online booking", v: "Self-serve with confirmation" },
    ],
    metrics: [
      { label: "Qualified leads", value: "+180%" },
      { label: "Ad spend", value: "-35%" },
      { label: "Booking conversion", value: "9.2%" },
    ],
  },
];

export const Portfolio = ({ onCTA }) => {
  const [active, setActive] = useState(null);

  return (
    <section
      id="work"
      data-testid="portfolio-section"
      className="relative py-24 sm:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div className="max-w-2xl">
            <div className="text-[11px] tracking-[0.2em] uppercase text-[#D4AF37] mb-4">
              Selected Work
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-light text-white">
              Case studies, <span className="gold-text italic">real numbers.</span>
            </h2>
          </div>
          <p className="max-w-md text-[#B0B0B0]">
            Three recent builds across retail, hospitality and healthcare — with
            before/after metrics, not just pretty mockups.
          </p>
        </div>

        <div className="space-y-12">
          {cases.map((c, i) => (
            <motion.article
              key={c.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`case-study-${i}`}
              className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-start bg-[#0A0A0A] border border-[#222] rounded-3xl p-6 sm:p-8 lg:p-10 hover:border-[#D4AF37]/30 transition-colors"
            >
              {/* Image */}
              <div
                className={`lg:col-span-6 ${
                  i % 2 === 1 ? "lg:order-2" : ""
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden border border-white/10 aspect-[16/11] bg-[#111]">
                  <img
                    src={c.image}
                    alt={c.title}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-black/60 via-black/20 to-transparent pointer-events-none" />
                  <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-4">
                    <div className="glass rounded-full px-3 py-1.5">
                      <span className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37]">
                        Case Study {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="glass-gold rounded-full px-3 py-1.5">
                      <span className="text-[10px] tracking-[0.18em] uppercase text-[#D4AF37]">
                        {c.accent}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Metrics row */}
                <div className="mt-5 grid grid-cols-3 gap-3">
                  {c.metrics.map((m) => (
                    <div
                      key={m.label}
                      className="bg-[#050505] border border-[#222] rounded-xl px-3 py-3 text-center"
                    >
                      <div className="font-display text-xl sm:text-2xl gold-text tracking-tight">
                        {m.value}
                      </div>
                      <div className="text-[10px] sm:text-xs text-[#737373] mt-1 uppercase tracking-wider">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Details */}
              <div className={`lg:col-span-6 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                <div className="text-xs text-[#737373] tracking-wider uppercase mb-2">
                  {c.client}
                </div>
                <h3 className="font-display text-3xl sm:text-4xl text-white tracking-tight">
                  {c.title}
                </h3>

                <div className="mt-6 flex items-start gap-3">
                  <Target className="w-4 h-4 text-[#D4AF37] mt-1 shrink-0" />
                  <div>
                    <div className="text-xs uppercase tracking-wider text-[#D4AF37] mb-1">
                      Client Goal
                    </div>
                    <p className="text-[#B0B0B0] leading-relaxed">{c.goal}</p>
                  </div>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-2">
                  <Clock className="w-3.5 h-3.5 text-[#737373]" />
                  <span className="text-xs text-[#737373] uppercase tracking-wider mr-2">
                    Delivered in {c.timeline} ·
                  </span>
                  {c.services.map((s) => (
                    <span
                      key={s}
                      className="text-[11px] tracking-wider text-[#B0B0B0] border border-[#222] rounded-full px-2.5 py-1"
                    >
                      {s}
                    </span>
                  ))}
                </div>

                {/* Before / After */}
                <div className="mt-7 grid sm:grid-cols-2 gap-3">
                  <div className="bg-[#050505] border border-[#222] rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#737373] mb-3">
                      Before
                    </div>
                    <ul className="space-y-2">
                      {c.before.map((b) => (
                        <li key={b.k} className="text-xs text-[#B0B0B0]">
                          <span className="text-[#737373]">{b.k}:</span>{" "}
                          <span className="text-white">{b.v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className="relative rounded-xl p-4 border"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.02) 100%)",
                      borderColor: "rgba(212,175,55,0.35)",
                    }}
                  >
                    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] mb-3">
                      After <TrendingUp className="w-3 h-3" />
                    </div>
                    <ul className="space-y-2">
                      {c.after.map((b) => (
                        <li key={b.k} className="text-xs text-[#B0B0B0]">
                          <span className="text-[#737373]">{b.k}:</span>{" "}
                          <span className="text-white">{b.v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <button
                  data-testid={`case-cta-${i}`}
                  onClick={() => setActive(i)}
                  className="mt-8 inline-flex items-center gap-1.5 text-sm text-white hover:text-[#D4AF37] transition-colors group"
                >
                  View full case study
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </button>
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* Case study popup */}
      <AnimatePresence>
        {active !== null && (
          <motion.div
            data-testid="case-modal"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[75] flex items-center justify-center p-4"
          >
            <div
              className="absolute inset-0 bg-black/85 backdrop-blur-sm"
              onClick={() => setActive(null)}
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              className="relative w-full max-w-3xl max-h-[88vh] overflow-y-auto bg-[#0A0A0A] border border-[#D4AF37]/30 rounded-3xl"
            >
              <button
                data-testid="case-modal-close"
                onClick={() => setActive(null)}
                className="absolute top-4 right-4 z-10 w-9 h-9 rounded-full border border-white/10 bg-black/40 flex items-center justify-center text-white hover:border-[#D4AF37]/50"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="relative aspect-[16/8] overflow-hidden">
                <img
                  src={cases[active].image}
                  alt={cases[active].title}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/40 to-transparent" />
                <div className="absolute bottom-5 left-6 right-6">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] mb-2">
                    {cases[active].client}
                  </div>
                  <h3 className="font-display text-3xl sm:text-4xl text-white">
                    {cases[active].title}
                  </h3>
                </div>
              </div>
              <div className="p-6 sm:p-8 space-y-6">
                <div>
                  <div className="text-xs uppercase tracking-wider text-[#D4AF37] mb-2">
                    Client Goal
                  </div>
                  <p className="text-[#B0B0B0] leading-relaxed">{cases[active].goal}</p>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {cases[active].metrics.map((m) => (
                    <div
                      key={m.label}
                      className="bg-[#050505] border border-[#222] rounded-xl px-3 py-4 text-center"
                    >
                      <div className="font-display text-2xl gold-text">{m.value}</div>
                      <div className="text-[10px] sm:text-xs text-[#737373] mt-1 uppercase tracking-wider">
                        {m.label}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <div className="bg-[#050505] border border-[#222] rounded-xl p-4">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#737373] mb-3">
                      Before
                    </div>
                    <ul className="space-y-2">
                      {cases[active].before.map((b) => (
                        <li key={b.k} className="text-xs text-[#B0B0B0]">
                          <span className="text-[#737373]">{b.k}:</span>{" "}
                          <span className="text-white">{b.v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div
                    className="rounded-xl p-4 border"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.02) 100%)",
                      borderColor: "rgba(212,175,55,0.35)",
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#D4AF37] mb-3">
                      After
                    </div>
                    <ul className="space-y-2">
                      {cases[active].after.map((b) => (
                        <li key={b.k} className="text-xs text-[#B0B0B0]">
                          <span className="text-[#737373]">{b.k}:</span>{" "}
                          <span className="text-white">{b.v}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                <button
                  data-testid="case-modal-cta"
                  onClick={() => {
                    setActive(null);
                    onCTA?.();
                  }}
                  className="btn-gold-glow w-full inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#E6C35C] text-black font-semibold rounded-full px-6 py-3.5 transition-all"
                >
                  Want results like this? <ArrowUpRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
